/// Merge a user's saved column order with the live column registry.
///
/// - Saved keys that still exist in the registry come first, in the
///   saved sequence (duplicates collapse to the first occurrence).
/// - Saved keys the registry no longer knows are dropped silently.
/// - Registry columns missing from the saved order are appended in
///   registry order, so newly added columns show up at the end instead
///   of breaking the layout.
///
/// A `null`/empty saved order returns the registry as-is. Inputs are
/// never mutated.
export function applyColumnOrder<T extends { key: string }>(
	registry: readonly T[],
	savedOrder: readonly string[] | null | undefined,
): T[] {
	if (!savedOrder || savedOrder.length === 0) return [...registry];

	const byKey = new Map(registry.map((c) => [c.key, c]));
	const seen = new Set<string>();
	const ordered: T[] = [];

	for (const key of savedOrder) {
		const col = byKey.get(key);
		if (col && !seen.has(key)) {
			ordered.push(col);
			seen.add(key);
		}
	}
	for (const col of registry) {
		if (!seen.has(col.key)) ordered.push(col);
	}
	return ordered;
}
